import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Pencil, Trash2 } from 'lucide-react';
import PrintButton from '../../utils/print';
import PrintLetterhead from './PrintLetterhead';
import { useCompany } from './useCompany';
import { makeMoneyFormatter, getDisplayCurrency, convertAmount } from '../../utils/currency';

const fmtRow = (row, value) => makeMoneyFormatter(row.currency, { currencyDisplay: 'code' })(value);

/**
 * Shared list page for Estimates, Sales Orders and Purchase Orders:
 * status filter, search, row actions (edit / delete) and optional
 * convert actions (to invoice, sales order or bill).
 */
export default function OrderList({
  title, api, basePath, icon, statuses = ['Draft'],
  numberField, partyField, dateField, secondField, secondLabel,
  convertActions = [], reopenAPI = null,
}) {
  const locked = ['Closed', 'Cancelled'];
  const isLocked = (row) => locked.includes(row.status);
  const company = useCompany();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [busyId, setBusyId] = useState(null);
  const listRef = useRef(null);

  const baseCurrency = company?.base_currency || getDisplayCurrency();
  const fmtBase = makeMoneyFormatter(baseCurrency, { currencyDisplay: 'code' });

  const load = (s = status, q = search) => {
    setLoading(true);
    api.list({ status: s || undefined, search: q || undefined })
      .then(setRows)
      .catch(e => toast.error(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [status]);

  const doDelete = async (row) => {
    if (!window.confirm(`Delete ${title.toLowerCase()} ${row[numberField]}?`)) return;
    try {
      await api.remove(row.id);
      toast.success(`${title} deleted`);
      load();
    } catch (err) { toast.error(err.message); }
  };

  const doConvert = async (action, row) => {
    setBusyId(row.id);
    try {
      const res = await action.handler(row.id);
      toast.success(`${title} converted to ${action.label}`);
      if (res && res.id) navigate(action.redirectTo(res.id));
      else load();
    } catch (err) { toast.error(err.message); }
    setBusyId(null);
  };

  const doReopen = async (row) => {
    setBusyId(row.id);
    try {
      await reopenAPI(row.id);
      toast.success(`${title.replace(/s$/, '')} reopened`);
      load();
    } catch (err) { toast.error(err.message); }
    setBusyId(null);
  };

  return (
    <div>
      <div className="toolbar">
        <h2>{icon} {title}</h2>
        <div className="toolbar-right">
          <input className="form-control" placeholder={`Search ${title.toLowerCase()}...`} value={search}
            onChange={e => setSearch(e.target.value)} onKeyDown={e => e.key === 'Enter' && load(status, search)} style={{ width: 250 }} />
          <PrintButton targetRef={listRef} title={`${title} - ${new Date().toLocaleDateString()}`} />
          <button className="btn btn-primary" onClick={() => navigate(`${basePath}/new`)}>+ New {title.replace(/s$/, '')}</button>
        </div>
      </div>

      <div className="filters-bar">
        {['', ...statuses].map(s => (
          <button key={s} className={`btn btn-sm ${status === s ? 'btn-primary' : 'btn-outline'}`} onClick={() => setStatus(s)}>{s || 'All'}</button>
        ))}
      </div>

      {loading ? <div className="spinner" style={{ margin: '40px auto' }} /> : (
        <div className="card" ref={listRef}>
          <div className="card-body" style={{ padding: 0 }}>
            <PrintLetterhead />
            <table>
              <thead>
                <tr>
                  <th>{numberField === 'po_number' ? 'PO #' : numberField === 'order_number' ? 'Order #' : 'Estimate #'}</th>
                  <th>{partyField === 'vendor_name' ? 'Vendor' : 'Customer'}</th>
                  <th>{dateField === 'order_date' ? 'Order Date' : dateField === 'estimate_date' ? 'Estimate Date' : 'Date'}</th>
                  <th>{secondLabel}</th>
                  <th>Currency</th>
                  <th className="text-right">Total</th>
                  <th>Status</th>
                  <th className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(row => (
                  <tr key={row.id} style={{ cursor: isLocked(row) ? 'default' : 'pointer' }}
                    onClick={() => { if (!isLocked(row)) navigate(`${basePath}/${row.id}/edit`); }}>
                    <td className="font-mono">{row[numberField]}</td>
                    <td>{row[partyField]}</td>
                    <td>{row[dateField]}</td>
                    <td>{row[secondField] || '—'}</td>
                    <td>{row.currency || '—'}</td>
                    <td className="amount">
                    {fmtRow(row, row.total)}
                    {row.currency && row.currency !== baseCurrency && parseFloat(row.total) > 0 && (
                      <div className="fx-sub">≈ {fmtBase(convertAmount(row.total, row.exchange_rate))}</div>
                    )}
                  </td>
                    <td><span className={`badge badge-${row.status?.toLowerCase()}`}>{row.status}</span></td>
                    <td className="text-right" onClick={e => e.stopPropagation()}>
                      <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                        {convertActions.map(a => (
                          <button key={a.label} className="btn btn-outline btn-sm" disabled={busyId === row.id}
                            onClick={() => doConvert(a, row)}>
                            {a.icon} {busyId === row.id ? '...' : a.label}
                          </button>
                        ))}
                        {isLocked(row) && reopenAPI ? (
                          <button className="btn btn-outline btn-sm" disabled={busyId === row.id}
                            title="Reopen this order to edit it" onClick={() => doReopen(row)}>
                            {busyId === row.id ? '...' : 'Reopen'}
                          </button>
                        ) : (
                          <button className="btn btn-ghost btn-sm" title="Edit" onClick={() => navigate(`${basePath}/${row.id}/edit`)}><Pencil size={13} /></button>
                        )}
                        <button className="btn btn-ghost btn-sm" title="Delete" onClick={() => doDelete(row)}><Trash2 size={13} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr><td colSpan={8} className="empty-state"><div className="empty-icon">{icon}</div><h3>No {title.toLowerCase()} yet</h3><p>Create your first {title.toLowerCase().replace(/s$/, '')} to get started.</p></td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
